import React, { useEffect, useState } from 'react';
import { Modal, Button, Spin, notification, theme } from 'antd';
import {
  NotificationConfig,
  StateConfig,
  Template,
  Channel,
  OWNER_TYPE,
  STATE_CATALOG,
  WSP_TEMPLATE_BY_STATE,
  buildDefaultTemplate,
  useNotificationConfig,
  useSaveNotificationConfig,
  useCreateTemplate,
  useTemplate,
} from 'services/NotificationTemplatesService';
import useStyles from './styles';
import ChannelSection from './ChannelSection';
import TemplateEditorModal from './TemplateEditorModal';
import PreviewModal from './PreviewModal';
import WspMappingModal from './WspMappingModal';

interface NotificationTemplatesModalProps {
  open: boolean;
  onClose: () => void;
  ownerType: number;
  ownerId: number;

  ownerName?: string;

  parentName?: string;

  parentOwnerId?: number;

  channel?: Channel;
}

interface EditorTarget {
  channel: Channel;
  state: StateConfig;
  template?: Template;
}

const isNumericId = (id?: string): boolean => !!id && /^\d+$/.test(id);

const mergeInheritedTemplateIds = (
  storeCfg: NotificationConfig,
  parentCfg?: NotificationConfig,
): NotificationConfig => {
  if (!parentCfg) return storeCfg;
  const parentByState = new Map(
    parentCfg.email.states.map((s) => [s.stateCode, s]),
  );
  return {
    ...storeCfg,
    email: {
      ...storeCfg.email,
      states: storeCfg.email.states.map((s) => {
        if (isNumericId(s.templateId)) return s;
        const parent = parentByState.get(s.stateCode);
        if (!parent || !isNumericId(parent.templateId)) return s;
        return {
          ...s,
          templateId: parent.templateId,
          templateName: parent.templateName,
        };
      }),
    },
  };
};

const NotificationTemplatesModal = ({
  open,
  onClose,
  ownerType,
  ownerId,
  ownerName,
  parentName,
  parentOwnerId,
  channel,
}: NotificationTemplatesModalProps) => {
  const { token } = theme.useToken();
  const classes = useStyles({ theme: token });

  const { data, isLoading } = useNotificationConfig(ownerType, ownerId, open);
  const saveMutation = useSaveNotificationConfig();
  const createMutation = useCreateTemplate();

  const isStore = ownerType === OWNER_TYPE.STORE;

  const { data: parentConfig } = useNotificationConfig(
    OWNER_TYPE.MARKETPLACE,
    parentOwnerId,
    open && isStore && !!parentOwnerId,
  );

  const [draft, setDraft] = useState<NotificationConfig | null>(null);
  const [editorTarget, setEditorTarget] = useState<EditorTarget | null>(null);
  const [previewTarget, setPreviewTarget] = useState<EditorTarget | null>(null);
  const [wspTarget, setWspTarget] = useState<StateConfig | null>(null);

  const editTemplateId =
    editorTarget?.channel === 'email'
      ? editorTarget.state.templateId
      : undefined;
  const { data: realTemplate, isFetching: isTemplateLoading } = useTemplate(
    editTemplateId,
    !!editorTarget,
  );

  const editorTemplate = realTemplate ?? editorTarget?.template;

  const previewTemplateId =
    previewTarget?.channel === 'email'
      ? previewTarget.state.templateId
      : undefined;
  const { data: realPreviewTemplate } = useTemplate(
    previewTemplateId,
    !!previewTarget,
  );
  const previewTemplate = realPreviewTemplate ?? previewTarget?.template;

  useEffect(() => {
    if (!data) return;

    setDraft(isStore ? mergeInheritedTemplateIds(data, parentConfig) : data);
  }, [data, parentConfig, isStore]);

  const isInheriting = !!draft?.isInheriting;
  const modalTitle =
    channel === 'email'
      ? 'Plantillas de mail'
      : channel === 'whatsapp'
        ? 'Plantillas de WhatsApp'
        : 'Plantillas de notificación';

  const updateChannelStates = (
    channel: Channel,
    updater: (states: StateConfig[]) => StateConfig[],
  ) => {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            [channel]: {
              ...prev[channel],
              states: updater(prev[channel].states),
            },
          }
        : prev,
    );
  };

  const handleToggleState = (
    channel: Channel,
    stateCode: number,
    enabled: boolean,
  ) => {
    updateChannelStates(channel, (states) =>
      states.map((s) => (s.stateCode === stateCode ? { ...s, enabled } : s)),
    );
  };

  const handleAddState = (channel: Channel, stateCode: number) => {
    const stateName = STATE_CATALOG[stateCode] ?? `Estado ${stateCode}`;
    const newState: StateConfig =
      channel === 'whatsapp'
        ? {
            stateCode,
            stateName,
            enabled: true,
            templateName: WSP_TEMPLATE_BY_STATE[stateCode] ?? '',
            wspTemplateName: WSP_TEMPLATE_BY_STATE[stateCode] ?? '',
          }
        : {
            stateCode,
            stateName,
            enabled: true,

            templateName: `Chazki — ${stateName}`,
          };
    updateChannelStates(channel, (states) =>
      [...states, newState].sort((a, b) => a.stateCode - b.stateCode),
    );
  };

  const handleRemoveState = (channel: Channel, stateCode: number) => {
    updateChannelStates(channel, (states) =>
      states.filter((s) => s.stateCode !== stateCode),
    );
  };

  const handleEditOrMap = (channel: Channel, state: StateConfig) => {
    if (channel === 'whatsapp') {
      setWspTarget(state);
      return;
    }
    setEditorTarget({ channel, state, template: buildDefaultTemplate(state) });
  };

  const handlePreview = (channel: Channel, state: StateConfig) => {
    setPreviewTarget({
      channel,
      state,
      template: channel === 'email' ? buildDefaultTemplate(state) : undefined,
    });
  };

  const handleTemplateSaved = async (template: Template) => {
    if (!editorTarget) return;

    const target = editorTarget;
    try {
      const saved = await createMutation.mutateAsync(template);
      updateChannelStates(target.channel, (states) =>
        states.map((s) =>
          s.stateCode === target.state.stateCode
            ? { ...s, templateId: saved.id, templateName: saved.name }
            : s,
        ),
      );
    } catch (error) {
      notification.error({
        message: 'No se pudo guardar la plantilla',
        description:
          error instanceof Error
            ? error.message
            : 'Error al crear la plantilla.',
      });
      throw error;
    }
  };

  const handleSaveConfig = async () => {
    if (!draft) return;

    const orphanMail = draft.email.states.filter(
      (s) => s.enabled && !/^\d+$/.test(s.templateId ?? ''),
    );
    if (orphanMail.length > 0) {
      notification.warning({
        message: 'Faltan plantillas',
        description: `Estos estados están activos pero no tienen plantilla asignada: ${orphanMail
          .map((s) => s.stateName)
          .join(
            ', ',
          )}. Editá cada uno para crear una plantilla, o desactivalo antes de guardar.`,
      });
      return;
    }
    try {
      await saveMutation.mutateAsync(draft);
      notification.success({
        message: 'Plantillas de notificación',
        description: 'La configuración se guardó correctamente.',
      });
      onClose();
    } catch (error) {
      notification.error({
        message: 'No se pudo guardar',
        description:
          error instanceof Error
            ? error.message
            : 'Error al guardar la configuración.',
      });
    }
  };

  const subtitle = [
    ownerName,
    'operador Chazki',
    'cada estado usa la plantilla asignada en la configuración',
    isStore ? undefined : 'se aplica a las tiendas sin plantillas propias',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <Modal
        title={
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{modalTitle}</div>
            <div
              style={{
                fontSize: 12.5,
                color: '#8c8c8c',
                marginTop: 3,
                fontWeight: 400,
              }}
            >
              {subtitle}
            </div>
          </div>
        }
        open={open}
        onCancel={onClose}
        width={960}
        styles={{
          body: {
            background: '#f5f7f8',
            maxHeight: '72vh',
            overflow: 'auto',
            padding: 18,
          },
        }}
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <div style={{ display: 'flex', gap: 10 }}>
              <Button onClick={onClose}>Cancelar</Button>
              <Button
                type="primary"
                loading={saveMutation.isPending}
                onClick={handleSaveConfig}
              >
                Guardar configuración
              </Button>
            </div>
          </div>
        }
      >
        <Spin spinning={isLoading || !draft}>
          {draft && (
            <>
              {isStore && isInheriting && (
                <div className={classes.inheritBanner}>
                  <span>
                    Esta tienda <b>hereda las plantillas</b>
                    {parentName ? ` de ${parentName}` : ' del marketplace'}. Al
                    guardar aquí se crea una configuración propia.
                  </span>
                </div>
              )}
              {(!channel || channel === 'email') && (
                <ChannelSection
                  classes={classes}
                  channel="email"
                  channelConfig={draft.email}
                  fullConfig={draft}
                  onToggleState={(code, enabled) =>
                    handleToggleState('email', code, enabled)
                  }
                  onAddState={(code) => handleAddState('email', code)}
                  onRemoveState={(code) => handleRemoveState('email', code)}
                  onPreview={(state) => handlePreview('email', state)}
                  onEditOrMap={(state) => handleEditOrMap('email', state)}
                />
              )}
              {(!channel || channel === 'whatsapp') && (
                <ChannelSection
                  classes={classes}
                  channel="whatsapp"
                  channelConfig={draft.whatsapp}
                  fullConfig={draft}
                  onToggleState={(code, enabled) =>
                    handleToggleState('whatsapp', code, enabled)
                  }
                  onAddState={(code) => handleAddState('whatsapp', code)}
                  onRemoveState={(code) => handleRemoveState('whatsapp', code)}
                  onPreview={(state) => handlePreview('whatsapp', state)}
                  onEditOrMap={(state) => handleEditOrMap('whatsapp', state)}
                />
              )}
            </>
          )}
        </Spin>
      </Modal>

      <TemplateEditorModal
        classes={classes}
        open={!!editorTarget}
        onClose={() => setEditorTarget(null)}
        onSave={handleTemplateSaved}
        stateName={editorTarget?.state.stateName ?? ''}
        template={editorTemplate}
        loading={isTemplateLoading && !realTemplate}
      />

      <PreviewModal
        classes={classes}
        open={!!previewTarget}
        onClose={() => setPreviewTarget(null)}
        channel={previewTarget?.channel ?? 'email'}
        stateName={previewTarget?.state.stateName ?? ''}
        template={previewTemplate}
        brandColor={previewTemplate?.brandColor ?? token.colorPrimary}
      />

      <WspMappingModal
        classes={classes}
        open={!!wspTarget}
        onClose={() => setWspTarget(null)}
        templateName={wspTarget?.wspTemplateName ?? ''}
        stateCode={wspTarget?.stateCode ?? 0}
        stateName={wspTarget?.stateName ?? ''}
      />
    </>
  );
};

export default NotificationTemplatesModal;
